import { forwardRef, useEffect, useRef, useState } from "react";
import { Section } from "../components/Layout/Section";
import { Container } from "../components/Layout/Container";
import styled from "@emotion/styled";
import { Divider } from "../components/Layout/Divider";
import { motion, useInView } from "framer-motion";
import SectionTitleStyled from "../components/SectionTitle";
import { ref as firestoreRef, getDownloadURL } from "@firebase/storage";
import { useApp } from "../AppProvider";

const SectionTitle = styled(SectionTitleStyled)(() => ({
  "@media (max-width: 768px)": {
    marginTop: 0,
  },
}));

const AddressTitle = styled("h2")(() => ({
  margin: 0,
  fontFamily: "Oswald",
  fontSize: "2.75rem",
}));
const AddressText = styled("p")(() => ({
  margin: 0,
  fontFamily: "Oswald",
  fontSize: "2rem",
  fontWeight: 300,
}));

const ImagesWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  columnGap: "2rem",
  rowGap: "5rem",
  marginTop: "3rem",
}));

const Image = styled(motion.img, {
  shouldForwardProp: (prop) => prop !== "percent",
})<{ percent: number }>(({ percent }) => ({
  width: "100%",
  willChange: "transform",
  borderRadius: 12,
  cursor: "pointer",
  objectFit: "cover",
  "@media (min-width: 768px)": {
    ...(percent !== 100 && {
      width: `calc(${percent}% - 2.5rem)`,
    }),
    ...(percent === 100 && {
      width: "100%",
    }),
  },
}));

const ImageText = styled("div", {
  shouldForwardProp: (prop) => prop !== "percent",
})<{ percent: number }>(({ percent }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "row",
  textAlign: "center",
  padding: "0 2rem",
  fontSize: 20,
  "@media (min-width: 768px)": {
    ...(percent !== 100 && {
      width: `calc(${percent}% - 2.5rem)`,
    }),
  },
}));

const TextItem = styled("p")(() => ({
  margin: 0,
  padding: 0,
  width: "100%",
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  textAlign: "left",
  "& span:first-of-type": {
    flex: 1,
  },
  "& span:last-of-type": {
    minWidth: 100,
    marginLeft: 10,
  },
}));

const Image4 = styled(Image)(() => ({
  "@media (min-width: 768px)": {
    width: "100%",
  },
  "@media (min-width: 1024px)": {
    width: "calc(40% - 2.5rem)",
  },
}));

const CenteredImageText = styled(ImageText)(() => ({
  flexDirection: "column",
  width: "100%",
  gap: "0.5rem",
  maxWidth: 800,
  marginLeft: "auto",
  marginRight: "auto",
  padding: 0,
}));

const Beschreibung = styled("p")(() => ({
  fontSize: 36,
  textAlign: "center",
  fontWeight: 400,
  color: "rgba(0,0,0,.8)",
  "@media (max-width: 768px)": {
    fontSize: 28,
  },
}));

export const Studio = forwardRef<HTMLDivElement, unknown>((props, ref) => {
  const container = useRef<HTMLDivElement>(null);
  const { storage } = useApp();
  const isInView = useInView(container);
  const [studio1, setStudio1] = useState<any>(null);
  const [studio2, setStudio2] = useState<any>(null);
  const [studio3, setStudio3] = useState<any>(null);
  const [studio4, setStudio4] = useState<any>(null);
  const [studio5, setStudio5] = useState<any>(null);

  useEffect(() => {
    if (!storage) {
      return;
    }

    let cancel = false;

    if (isInView) {
      getDownloadURL(firestoreRef(storage, "studio/studio.webp")).then(
        (url) => {
          if (!cancel) {
            setStudio1(url);
          }
        }
      );
      getDownloadURL(firestoreRef(storage, "studio/studio2.webp")).then(
        (url) => {
          if (!cancel) {
            setStudio2(url);
          }
        }
      );
      getDownloadURL(firestoreRef(storage, "studio/studio3.webp")).then(
        (url) => {
          if (!cancel) {
            setStudio3(url);
          }
        }
      );
      getDownloadURL(firestoreRef(storage, "studio/studio4.webp")).then(
        (url) => {
          if (!cancel) {
            setStudio4(url);
          }
        }
      );
      getDownloadURL(firestoreRef(storage, "studio/studio5.webp")).then(
        (url) => {
          if (!cancel) {
            setStudio5(url);
          }
        }
      );
    }

    return () => {
      cancel = true;
    };
  }, [isInView, storage]);

  return (
    <Section ref={ref} id={"studio"}>
      <Container ref={container}>
        <SectionTitle>Unser Studio</SectionTitle>
        <Beschreibung>
          Wenn ihr eure Film- oder Fotoprojekte lieber selbst in die Hand nehmen
          möchtet und euch kreativ ausleben wollt, dann könnt ihr das Film- und
          Fotostudio von CentreFilms mieten. Auf einer Fläche von 250qm, mit
          einer Hohlkehle integriert und einer seperaten Fotoleinwand, lassen
          sich die verschiedensten Projektideen umsetzen. Auf Anfrage können wir
          euch auch mit der perfekten Beleuchtung, wie auch namenhaften
          Film-/Fotoequipment ausstatten.
        </Beschreibung>
        <p style={{ fontSize: 20 }}>
          <i>– feel free –</i>
        </p>
        <Divider />
        <h1>#doyourownthing</h1>
        <AddressTitle>CENTRE FILMS STUDIO RADEBEUL</AddressTitle>
        <AddressText>Meißner Str. 21</AddressText>
        <AddressText>01445 Radebeul</AddressText>
        <ImagesWrapper>
          <Image
            initial={{ y: "20%", opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            src={studio1}
            alt={"studio-1"}
            percent={40}
          />
          <ImageText percent={60}>
            Viel Raum und viel Licht zur Umsetzung von Foto- und
            Videoproduktionen, Workshops, Events und Castings und Radebeul
            Stadtgrenze Dresden
          </ImageText>
          <ImageText percent={40}>
            Mit einer Hohlkehle von 10m x 3,50m x 4m und vielen weiteren
            Möglichkeiten
          </ImageText>
          <Image
            initial={{ y: "20%", opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            src={studio2}
            alt={"studio-2"}
            percent={60}
          />
          <Image
            initial={{ y: "20%", opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            src={studio3}
            alt={"studio-3"}
            percent={70}
          />
          <ImageText percent={30}>
            <ul
              style={{
                textAlign: "left",
                width: "100%",
                margin: 0,
                padding: 0,
              }}
            >
              <li>große streichbare Hohlkehle 10m x 3,50m x 4m</li>
              <li>weitere Fotowand mit Papier</li>
              <li>Raum für die Visagistin / Maskenbildnerin</li>
              <li>Umkleide für Schauspieler</li>
              <li>Technikmiete vor Ort nach Absprache</li>
              <li>DSL vor Ort</li>
              <li>beste Erreichbarkeit über Autobahn oder Strassenbahn</li>
            </ul>
          </ImageText>
          <CenteredImageText percent={70}>
            <TextItem>
              <span>Fotoaufnahmen Tag</span>
              <span>8h</span>
              <span>300,00€</span>
            </TextItem>
            <TextItem>
              <span>Fotoaufnahmen halber Tag</span>
              <span>4h</span>
              <span>170,00€</span>
            </TextItem>
            <TextItem>
              <span>Videoproduktion Tag</span>
              <span>8h</span>
              <span>400,00€</span>
            </TextItem>
            <TextItem>
              <span>Videoproduktion halber Tag</span>
              <span>4h</span>
              <span>200,00€</span>
            </TextItem>
            <TextItem>
              <span>Events/Veranstaltungen</span>
              <span>nach Absprache</span>
              <span></span>
            </TextItem>
            <TextItem>
              <span>Castings</span>
              <span>nach Absprache</span>
              <span></span>
            </TextItem>
            <TextItem>
              <span>Streichen der gesamten Fläche:</span>
              <span>125,00€</span>
              <span></span>
            </TextItem>
            <TextItem>
              vorhandene Farben: weiß/grün (RAL 6018 für Greenscreen)/blau/grau
            </TextItem>
            <TextItem>
              Technik: Licht und Kameras können vor Ort gemietet werden. Weitere
              Infos unter: www.centrefilms.de/vermietung
            </TextItem>
            <TextItem>RED Kameras auf Anfrage.</TextItem>
          </CenteredImageText>
          <Image4
            initial={{ y: "20%", opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            src={studio4}
            alt={"studio-4"}
            percent={30}
          />
          <Image
            initial={{ y: "20%", opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            src={studio5}
            alt={"studio-5"}
            percent={100}
          />
          <ImageText percent={100}>
            Für Unterhaltung ist auch gesorgt. Kicker und Billardtisch sind vor
            Ort
          </ImageText>
        </ImagesWrapper>
      </Container>
    </Section>
  );
});
